import {FC, useMemo} from 'react';
import {Box, Typography} from '@mui/material';
import Button from '@/shared/components/Button';
import {SUBTITLE1, TEXT} from '@/utils/constants';
import {IRaffleTicketsData} from '@/utils/types';
import {raffleTicketItem} from './raffleTicketItem.styles';
import {ReactComponent as PlusIcon} from '@/assets/icons/plus.svg';
import {ReactComponent as MinusIcon} from '@/assets/icons/minus.svg';
import {useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {useDispatch} from 'react-redux';
import {setRaffleTickets} from '@/store/raffle';
import {toast} from 'react-toastify';
import {currencyFormatter} from '@/utils/helpers/currency';

interface RaffleTicketItemProps {
	tier: IRaffleTicketsData;

	raffleData: IRaffleTicketsData[];
}

const RaffleTicketItem: FC<RaffleTicketItemProps> = ({tier, raffleData}) => {
	const dispatch = useDispatch();

	const event = useAppSelector(getCurrentEventSelector);
	const remainingQty = event?.raffleInfo?.remainingQty;

	const isSoldOut = remainingQty !== null && remainingQty !== undefined && remainingQty <= 0;

	const isUnlimited = remainingQty === null || remainingQty === undefined;

	const subtotal = useMemo(() => tier!.amount * tier.price, [tier.amount, tier.price]);

	const handleAddTable = () => {
		if (isSoldOut) return;

		if (!isUnlimited && (tier.amount + 1) * tier.qty > (remainingQty ?? 0)) {
			toast.error('You have reached the maximum number of tickets for this raffle.');
			return;
		}

		const newRaffleData = raffleData.map((item) =>
			item.id === tier.id ? {...item, amount: item.amount + 1} : {...item, amount: 0}
		);
		dispatch(setRaffleTickets(newRaffleData));
	};

	const handleDeleteTable = () => {
		if (tier.amount <= 0) {
			return;
		}

		const newRaffleData = raffleData.map((item) => (item.id === tier.id ? {...item, amount: item.amount - 1} : item));
		dispatch(setRaffleTickets(newRaffleData));
	};

	return (
		<Box sx={raffleTicketItem.wrapper}>
			<Box sx={raffleTicketItem.column}>
				{`${tier.qty} for
				${currencyFormatter.format(tier.price)}`}
			</Box>

			<Box sx={raffleTicketItem.counterWrap}>
				<Button
					variant={TEXT}
					startIcon={<MinusIcon onClick={handleDeleteTable} />}
					disableRipple
					sx={isSoldOut ? raffleTicketItem.btnDisabled : raffleTicketItem.btn}
					disabled={isSoldOut}
				/>

				<Box sx={isSoldOut ? raffleTicketItem.counterDisabled : raffleTicketItem.counter}>{tier.amount}</Box>

				<Button
					variant={TEXT}
					endIcon={<PlusIcon onClick={handleAddTable} />}
					disableRipple
					sx={isSoldOut ? raffleTicketItem.btnDisabled : raffleTicketItem.btn}
					disabled={isSoldOut}
				/>
			</Box>
			<Box sx={[raffleTicketItem.column, raffleTicketItem.lastColumn]}>
				<Typography sx={raffleTicketItem.subtotal} variant={SUBTITLE1}>
					{currencyFormatter.format(subtotal)}
				</Typography>
			</Box>
		</Box>
	);
};

export default RaffleTicketItem;
